import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { RouteFallback } from '@/routes/lazy';
import {
  BadgeCheck,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Compass,
  CreditCard,
  FileText,
  Files,
  BookOpen,
  ClipboardCheck,
  Folder,
  Gift,
  GraduationCap,
  HelpCircle,
  Home,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Megaphone,
  Menu,
  Network,
  Radar,
  FileSpreadsheet,
  UserMinus,
  MessageSquare,
  Pin,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  User,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationApiService } from '@/lib/services/notification-api';
import { ConnectionsApiService } from '@/lib/services/connections-api';
import type {
  Notification as AppNotification,
  NotificationType,
} from '@/lib/services/notification-api';
import { formatRelativeTime } from '@/lib/utils/format-helpers';
import { useAuthStore } from '@/features/auth/store';
import { useMessagingRealtime, useRealtimeConnected } from '@/hooks/useMessagingRealtime';
import { badgeUnread } from '@/components/messaging/chat-utils';
import { useLogout } from '@/features/auth/api';
import { useUIStore } from '@/store/ui-store';
import { OrganizationApiService } from '@/lib/services/organization-api';
import { AcademicApiService } from '@/lib/services/academic-api';
import { CollegeWorkspaceApiService } from '@/lib/services/college-workspace-api';
import { CampusPlacementApiService } from '@/lib/services/placement-api';
import { MessageApiService } from '@/lib/services/message-api';
import { canUseCollegeSection, type CollegeSection } from '@/lib/college-access';
import { canUseCompanySection, type CompanySection } from '@/lib/company-access';
import {
  isStudentMembership,
  isWorkspaceMembership,
  memberRoleLabel,
  membershipHome,
  STUDENT_HOME,
} from '@/lib/workspace';
import { stripOrgSlug } from '@/lib/org-path';
import { setupProgress } from '@/lib/college-setup';
import { useCollegeRole, useCompanyRole, useSelectedOrgRole } from '@/hooks/useCollegeRole';
import type { UserOrganization } from '@/lib/services/organization-api';
import { Portal } from '@/components/core/Portal';
import { useBillingOverview } from '@/features/billing/hooks/useBilling';
import { useIsMobile } from '@/hooks/useIsMobile';
import { WlAvatar } from './WlPrimitives';
import '@/styles/app-shell.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type WorkspaceCtx = 'personal' | 'college' | 'company';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  badge?: string;
  /** Not built yet: shown with a "Soon" tag, still navigable to its preview page. */
  soon?: boolean;
  /** College pages only: which access rule governs the item. */
  section?: CollegeSection;
  /** Company pages only: which access rule governs the item. */
  companySection?: CompanySection;
  /** Personal pages only: shown just to students of a college, or just to everyone else. */
  audience?: 'student' | 'non-student';
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

// ─── Nav data ─────────────────────────────────────────────────────────────────

const I = { size: 17 };

const NAV: Record<WorkspaceCtx, NavGroup[]> = {
  personal: [
    {
      group: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <Home {...I} />, to: '/personal/dashboard' },
      ],
    },
    {
      group: 'Career',
      items: [
        { id: 'profile', label: 'My Profile', icon: <User {...I} />, to: '/personal/profile' },
        {
          id: 'skills',
          label: 'Assessments',
          icon: <Sparkles {...I} />,
          to: '/personal/skills-and-assessments',
        },
        { id: 'resume', label: 'Resume', icon: <FileText {...I} />, to: '/personal/resume' },
        {
          id: 'coverletter',
          label: 'Cover Letters',
          icon: <Files {...I} />,
          to: '/personal/cover-letters',
        },
      ],
    },
    {
      group: 'Jobs',
      items: [
        { id: 'discover', label: 'Discover', icon: <Compass {...I} />, to: '/personal/jobs' },
        {
          id: 'applications',
          label: 'Applications',
          icon: <Briefcase {...I} />,
          to: '/personal/applications',
        },
      ],
    },
    {
      // A student's college lives here, next to their career — not in the
      // college's staff workspace (see lib/workspace.ts).
      group: 'My College',
      items: [
        {
          id: 'my-college',
          label: 'College Home',
          icon: <GraduationCap {...I} />,
          to: '/personal/my-college',
          audience: 'student',
        },
        {
          id: 'campus-drives',
          label: 'Campus Drives',
          icon: <Building2 {...I} />,
          to: '/personal/drives',
          audience: 'student',
        },
        {
          id: 'offers',
          label: 'Offers',
          icon: <BadgeCheck {...I} />,
          to: '/personal/offers',
          audience: 'student',
        },
        {
          id: 'notices',
          label: 'Notices',
          icon: <Megaphone {...I} />,
          to: '/personal/notices',
          audience: 'student',
        },
      ],
    },
    {
      group: 'Tools',
      items: [
        {
          id: 'messages',
          label: 'Messages',
          icon: <MessageSquare {...I} />,
          to: '/personal/messages',
        },
        {
          id: 'insights',
          label: 'AI Insights',
          icon: <Sparkles {...I} />,
          to: '/personal/insights',
        },
        { id: 'documents', label: 'Documents', icon: <Folder {...I} />, to: '/personal/documents' },
        {
          id: 'find-college',
          label: 'Find your college',
          icon: <GraduationCap {...I} />,
          to: '/personal/find-college',
          audience: 'non-student',
        },
      ],
    },
    {
      group: 'Account',
      items: [
        {
          id: 'billing',
          label: 'Billing',
          icon: <CreditCard {...I} />,
          to: '/personal/subscription-and-billing',
        },
        { id: 'settings', label: 'Settings', icon: <Settings {...I} />, to: '/personal/settings' },
        { id: 'help', label: 'Help', icon: <HelpCircle {...I} />, to: '/help' },
      ],
    },
  ],
  // Items carry a `section` so the sidebar can hide what the member's role can't
  // use (see lib/college-access.ts); badges for setup and opt-outs are live.
  college: [
    {
      group: 'Overview',
      items: [
        {
          id: 'college-dashboard',
          section: 'dashboard',
          label: 'Dashboard',
          icon: <LayoutDashboard {...I} />,
          to: '/college/dashboard',
        },
        {
          id: 'college-setup',
          section: 'setup',
          label: 'Finish setup',
          icon: <ListChecks {...I} />,
          to: '/college/setup',
        },
      ],
    },
    {
      group: 'People',
      items: [
        {
          id: 'college-students',
          section: 'students',
          label: 'Students',
          icon: <Users {...I} />,
          to: '/college/students',
        },
        {
          id: 'college-staff',
          section: 'staff',
          label: 'Staff & Faculty',
          icon: <UserCog {...I} />,
          to: '/college/staff',
        },
        {
          id: 'college-structure',
          section: 'structure',
          label: 'Batches & Structure',
          icon: <Network {...I} />,
          to: '/college/structure',
        },
      ],
    },
    {
      group: 'Academics',
      items: [
        {
          id: 'college-records',
          section: 'records',
          label: 'Academic Records',
          icon: <FileSpreadsheet {...I} />,
          to: '/college/records',
        },
        {
          id: 'college-skill-insights',
          section: 'insights',
          label: 'Skill Insights',
          icon: <Radar {...I} />,
          to: '/college/skill-insights',
        },
        {
          id: 'college-documents',
          section: 'documents',
          label: 'Student Documents',
          icon: <Files {...I} />,
          to: '/college/documents',
        },
        {
          id: 'college-assessments',
          section: 'assessments',
          label: 'Assessments',
          icon: <BookOpen {...I} />,
          to: '/college/assessments',
        },
        {
          id: 'college-resume-reviews',
          section: 'resumeReviews',
          label: 'Resume Reviews',
          icon: <ClipboardCheck {...I} />,
          to: '/college/students/resume-reviews',
        },
        {
          id: 'college-careers',
          section: 'careers',
          label: 'Career Profiles',
          icon: <User {...I} />,
          to: '/college/students/profiles',
        },
        {
          id: 'college-reports',
          section: 'reports',
          label: 'Reports',
          icon: <BarChart3 {...I} />,
          to: '/college/assessments/reports',
        },
      ],
    },
    {
      group: 'Placements',
      items: [
        {
          id: 'college-drives',
          section: 'drives',
          label: 'Campus Drives',
          icon: <Briefcase {...I} />,
          to: '/college/drives',
        },
        {
          id: 'college-companies',
          section: 'companies',
          label: 'Partner Companies',
          icon: <Building2 {...I} />,
          to: '/college/companies',
        },
        {
          id: 'college-opt-outs',
          section: 'optouts',
          label: 'Opt-outs',
          icon: <UserMinus {...I} />,
          to: '/college/opt-outs',
        },
        {
          id: 'college-analytics',
          section: 'analytics',
          label: 'Placement Analytics',
          icon: <TrendingUp {...I} />,
          to: '/college/analytics',
        },
      ],
    },
    {
      group: 'Communication',
      items: [
        {
          id: 'college-notices',
          section: 'notices',
          label: 'Noticeboard',
          icon: <Megaphone {...I} />,
          to: '/college/notices',
        },
        {
          id: 'college-messages',
          section: 'messages',
          label: 'Messages',
          icon: <MessageSquare {...I} />,
          to: '/college/messages',
        },
      ],
    },
    {
      group: 'Institution',
      items: [
        {
          id: 'college-settings',
          section: 'settings',
          label: 'Settings',
          icon: <Settings {...I} />,
          to: '/college/settings',
        },
        {
          id: 'college-billing',
          section: 'billing',
          label: 'Billing',
          icon: <CreditCard {...I} />,
          to: '/college/billing',
        },
      ],
    },
  ],
  // Like the college nav, items carry an access rule (lib/company-access.ts).
  company: [
    {
      group: 'Overview',
      items: [
        {
          id: 'company-dashboard',
          companySection: 'dashboard',
          label: 'Dashboard',
          icon: <LayoutDashboard {...I} />,
          to: '/company/dashboard',
        },
      ],
    },
    {
      group: 'Hiring',
      items: [
        {
          id: 'company-openings',
          companySection: 'jobs',
          label: 'Job Openings',
          icon: <Briefcase {...I} />,
          to: '/company/jobs',
        },
        {
          id: 'company-applications',
          companySection: 'candidates',
          label: 'Applications',
          icon: <Files {...I} />,
          to: '/company/candidates',
        },
        {
          id: 'company-pipeline',
          companySection: 'pipeline',
          label: 'Pipeline',
          icon: <TrendingUp {...I} />,
          to: '/company/pipeline',
        },
      ],
    },
    {
      group: 'Tools',
      items: [
        {
          id: 'company-messages',
          companySection: 'messages',
          label: 'Messages',
          icon: <MessageSquare {...I} />,
          to: '/company/messages',
        },
        {
          id: 'company-team',
          companySection: 'team',
          label: 'Team',
          icon: <Users {...I} />,
          to: '/company/organization',
        },
        {
          id: 'company-analytics',
          companySection: 'analytics',
          label: 'Analytics',
          icon: <BarChart3 {...I} />,
          to: '/company/analytics',
        },
        {
          id: 'company-billing',
          companySection: 'billing',
          label: 'Billing',
          icon: <CreditCard {...I} />,
          to: '/company/billing',
        },
        {
          id: 'company-settings',
          companySection: 'settings',
          label: 'Settings',
          icon: <Settings {...I} />,
          to: '/company/settings',
        },
      ],
    },
  ],
};

// Breadcrumb map — exact matches first, prefix entries used as fallback for dynamic routes
const CRUMBS: Record<string, string[]> = {
  '/personal/dashboard': ['Personal', 'Dashboard'],
  '/personal/profile': ['Personal', 'Career Profile', 'My Profile'],
  '/personal/profile/edit': ['Personal', 'Career Profile', 'Edit Profile'],
  '/personal/profile/experience': ['Personal', 'Career Profile', 'Work Experience'],
  '/personal/profile/education': ['Personal', 'Career Profile', 'Education'],
  '/personal/profile/projects': ['Personal', 'Career Profile', 'Projects'],
  '/personal/profile/certifications': ['Personal', 'Career Profile', 'Certifications'],
  '/personal/profile/summary': ['Personal', 'Career Profile', 'Professional Summary'],
  '/personal/profile/skills': ['Personal', 'Career Profile', 'Skills'],
  '/personal/profile/languages': ['Personal', 'Career Profile', 'Languages'],
  '/personal/skills-and-assessments': ['Personal', 'Assessments'],
  '/personal/resume': ['Personal', 'Resume Studio'],
  '/personal/resume/builder': ['Personal', 'Resume Studio', 'Builder'],
  '/personal/cover-letters': ['Personal', 'Cover Letters'],
  '/personal/cover-letters/builder': ['Personal', 'Cover Letters', 'Builder'],
  '/personal/jobs': ['Personal', 'Jobs', 'Discover'],
  '/personal/applications': ['Personal', 'Jobs', 'Applications'],
  '/personal/messages': ['Personal', 'Messages'],
  '/personal/connections': ['Personal', 'ConnectHub'],
  '/personal/people': ['Personal', 'ConnectHub', 'Profile'],
  '/personal/insights': ['Personal', 'AI Insights'],
  '/personal/documents': ['Personal', 'Documents'],
  '/personal/find-college': ['Personal', 'Find your college'],
  '/personal/my-college': ['Personal', 'My College'],
  '/personal/drives': ['Personal', 'My College', 'Campus Drives'],
  '/personal/offers': ['Personal', 'My College', 'Offers'],
  '/personal/notices': ['Personal', 'My College', 'Notices'],
  '/personal/settings': ['Personal', 'Settings'],
  '/personal/subscription-and-billing': ['Personal', 'Settings', 'Billing'],
  '/personal/settings/privacy': ['Personal', 'Settings', 'Privacy & Data'],
  '/personal/settings/storage': ['Personal', 'Settings', 'Storage'],
  '/personal/settings/merge-accounts': ['Personal', 'Settings', 'Merge accounts'],
  '/organization/create': ['Organizations', 'Create Organization'],
  '/help': ['Help & Support'],
  '/college/dashboard': ['College', 'Dashboard'],
  '/college/setup': ['College', 'Finish setup'],
  '/college/students': ['College', 'People', 'Students'],
  '/college/students/invite': ['College', 'People', 'Students', 'Invite'],
  '/college/students/import': ['College', 'People', 'Students', 'Import'],
  '/college/staff': ['College', 'People', 'Staff & Faculty'],
  '/college/staff/invite': ['College', 'People', 'Staff & Faculty', 'Invite'],
  '/college/structure': ['College', 'People', 'Batches & Structure'],
  '/college/structure/batches': ['College', 'People', 'Batches & Structure', 'Batch'],
  '/college/records': ['College', 'Academics', 'Academic Records'],
  '/college/skill-insights': ['College', 'Academics', 'Skill Insights'],
  '/college/students/profiles': ['College', 'Academics', 'Career Profiles'],
  '/college/assessments/reports': ['College', 'Academics', 'Reports'],
  '/college/drives': ['College', 'Placements', 'Campus Drives'],
  '/college/drives/create': ['College', 'Placements', 'Campus Drives', 'New drive'],
  '/college/companies': ['College', 'Placements', 'Partner Companies'],
  '/college/opt-outs': ['College', 'Placements', 'Opt-outs'],
  '/college/documents': ['College', 'Academics', 'Student Documents'],
  '/college/assessments': ['College', 'Academics', 'Assessments'],
  '/college/students/resume-reviews': ['College', 'Academics', 'Resume Reviews'],
  '/personal/resume/reviews': ['Personal', 'Resume Studio', 'Reviews'],
  '/college/companies/recruiters': ['College', 'Placements', 'Recruiter Contacts'],
  '/college/analytics': ['College', 'Placements', 'Placement Analytics'],
  '/college/notices': ['College', 'Communication', 'Noticeboard'],
  '/college/notices/new': ['College', 'Communication', 'Noticeboard', 'New notice'],
  '/college/messages': ['College', 'Communication', 'Messages'],
  '/college/settings': ['College', 'Institution', 'Settings'],
  '/college/billing': ['College', 'Institution', 'Billing'],
  '/company/dashboard': ['Company', 'Dashboard'],
  '/company/jobs': ['Company', 'Hiring', 'Job Openings'],
  '/company/candidates': ['Company', 'Hiring', 'Applications'],
  '/company/pipeline': ['Company', 'Hiring', 'Pipeline'],
  '/company/messages': ['Company', 'Messages'],
  '/company/organization': ['Company', 'Team & Organization'],
  '/company/analytics': ['Company', 'Analytics'],
  '/company/settings': ['Company', 'Settings'],
  '/company/billing': ['Company', 'Billing'],
};

function resolveCrumbs(fullPathname: string): string[] {
  // Workspace URLs name the organization; the crumbs are keyed by page.
  const pathname = stripOrgSlug(fullPathname);
  if (CRUMBS[pathname]) return CRUMBS[pathname];
  // Fallback: find the longest prefix match for dynamic routes (e.g. /resume/builder/:id)
  const sorted = Object.keys(CRUMBS).sort((a, b) => b.length - a.length);
  for (const key of sorted) {
    if (pathname.startsWith(key + '/')) return CRUMBS[key];
  }
  return ['WeLynk'];
}

function getCtxFromPath(path: string): WorkspaceCtx {
  if (path.startsWith('/college')) return 'college';
  if (path.startsWith('/company')) return 'company';
  return 'personal';
}

// ─── Avatar helper ────────────────────────────────────────────────────────────

// ─── Search overlay ───────────────────────────────────────────────────────────

function SearchOverlay({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const allGroups = [
    {
      group: 'Pages',
      items: [
        { icon: <Home size={14} />, label: 'Dashboard', to: '/personal/dashboard' },
        { icon: <Compass size={14} />, label: 'Discover Jobs', to: '/personal/jobs' },
        { icon: <Briefcase size={14} />, label: 'Applications', to: '/personal/applications' },
        { icon: <User size={14} />, label: 'My Profile', to: '/personal/profile' },
        { icon: <Users size={14} />, label: 'ConnectHub', to: '/personal/connections' },
        { icon: <FileText size={14} />, label: 'Resume', to: '/personal/resume' },
        { icon: <Sparkles size={14} />, label: 'AI Insights', to: '/personal/insights' },
      ],
    },
  ]
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => !q || i.label.toLowerCase().includes(q.toLowerCase())),
    }))
    .filter((g) => g.items.length);

  return (
    <Portal>
      <div className="wl-overlay" onClick={onClose}>
        <div className="wl-search-modal" onClick={(e) => e.stopPropagation()}>
          <div className="wl-search-input-wrap">
            <Search size={18} style={{ color: 'var(--ink-4)', flexShrink: 0 }} />
            <input
              ref={inputRef}
              placeholder="Search pages, jobs, candidates…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span
              style={{
                fontSize: 11,
                color: 'var(--ink-5)',
                border: '1px solid var(--line)',
                borderRadius: 4,
                padding: '2px 6px',
              }}
            >
              ESC
            </span>
          </div>
          <div className="wl-search-results wl-scroll">
            {!allGroups.length && (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-4)' }}>
                No matches for "{q}"
              </div>
            )}
            {allGroups.map((g, i) => (
              <div key={i}>
                <div className="wl-search-group-title">{g.group}</div>
                {g.items.map((it, j) => (
                  <div
                    key={j}
                    className="wl-search-result"
                    onClick={() => {
                      navigate(it.to);
                      onClose();
                    }}
                  >
                    <div className="ic">{it.icon}</div>
                    <div>{it.label}</div>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-5)' }}>
                      ↵
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ─── Notification bell + dropdown ────────────────────────────────────────────

function getNotifIcon(type: NotificationType): React.ReactNode {
  switch (type) {
    case 'APPLICATION_UPDATE':
      return <Briefcase size={15} />;
    case 'INTERVIEW_SCHEDULED':
    case 'INTERVIEW_REMINDER':
      return <Calendar size={15} />;
    case 'MESSAGE_RECEIVED':
      return <MessageSquare size={15} />;
    case 'AI_INSIGHT_READY':
      return <Sparkles size={15} />;
    case 'OFFER_RECEIVED':
    case 'OFFER_ACCEPTED':
    case 'OFFER_REJECTED':
      return <Gift size={15} />;
    default:
      return <Bell size={15} />;
  }
}

function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => NotificationApiService.getUnreadCount(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: notifData, isLoading: notifLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => NotificationApiService.getNotifications({ pageSize: 20 }),
    enabled: isOpen,
    staleTime: 30_000,
  });

  const notifications = notifData?.items ?? [];

  const markReadMutation = useMutation({
    mutationFn: (ids: string[]) => NotificationApiService.markAsRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => NotificationApiService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen]);

  const displayCount = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null;

  function handleNotifClick(notif: AppNotification) {
    if (!notif.isRead) markReadMutation.mutate([notif.id]);
    if (notif.data?.actionUrl) {
      setIsOpen(false);
      navigate(notif.data.actionUrl);
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        className="wl-btn-icon"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Notifications"
        style={{ position: 'relative' }}
      >
        <Bell size={16} />
        {displayCount && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              padding: '0 3px',
              background: 'var(--teal)',
              color: '#fff',
              borderRadius: 99,
              fontSize: 9,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #fff',
              lineHeight: 1,
              boxSizing: 'border-box',
            }}
          >
            {displayCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="wl-notif-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 360,
            background: '#fff',
            border: '1px solid #e3e8f0',
            borderRadius: 12,
            boxShadow: '0 4px 16px -4px rgba(15,23,42,0.12)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 480,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px 12px',
              borderBottom: '1px solid var(--line-2)',
              flexShrink: 0,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14 }}>Notifications</div>
            <button
              className="wl-btn wl-btn-ghost wl-btn-sm"
              style={{ fontSize: 12 }}
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending || unreadCount === 0}
            >
              Mark all as read
            </button>
          </div>

          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--line-2)',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: 'var(--bg-2)',
                      flexShrink: 0,
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        height: 13,
                        width: '65%',
                        borderRadius: 4,
                        background: 'var(--bg-2)',
                        marginBottom: 6,
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                    <div
                      style={{
                        height: 11,
                        width: '85%',
                        borderRadius: 4,
                        background: 'var(--bg-2)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 24px',
                  gap: 10,
                }}
              >
                <Bell size={28} style={{ color: 'var(--ink-5)' }} />
                <div style={{ fontSize: 13, color: 'var(--ink-4)', fontWeight: 500 }}>
                  You're all caught up
                </div>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '12px 16px 12px 20px',
                    borderBottom: '1px solid var(--line-2)',
                    background: notif.isRead ? 'transparent' : 'var(--bg-2)',
                    cursor: notif.data?.actionUrl || !notif.isRead ? 'pointer' : 'default',
                    position: 'relative',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={(e) => {
                    if (!notif.isRead || notif.data?.actionUrl)
                      e.currentTarget.style.background = 'var(--bg-3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notif.isRead ? 'transparent' : 'var(--bg-2)';
                  }}
                >
                  {!notif.isRead && (
                    <span
                      style={{
                        position: 'absolute',
                        left: 7,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: 'var(--teal)',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: 'var(--bg-2)',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      color: 'var(--ink-3)',
                    }}
                  >
                    {getNotifIcon(notif.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: notif.isRead ? 400 : 600,
                        marginBottom: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {notif.title}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-4)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {notif.message}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-5)', marginTop: 4 }}>
                      {formatRelativeTime(notif.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ConnectHub header button ─────────────────────────────────────────────────

function ConnectHubButton() {
  const navigate = useNavigate();

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['connections', 'pending-received', 'count'],
    queryFn: () => ConnectionsApiService.listPendingReceived({ page: 1, pageSize: 1 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    select: (d) => d.total,
  });

  const displayCount = pendingCount > 99 ? '99+' : pendingCount > 0 ? String(pendingCount) : null;

  return (
    <button
      className="wl-btn-icon"
      onClick={() => navigate('/personal/connections')}
      aria-label={
        displayCount
          ? `ConnectHub — ${pendingCount} pending connection request${pendingCount === 1 ? '' : 's'}`
          : 'ConnectHub'
      }
      title="ConnectHub"
      style={{ position: 'relative' }}
    >
      <Users size={16} />
      {displayCount && (
        <span
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            padding: '0 3px',
            background: 'var(--teal)',
            color: '#fff',
            borderRadius: 99,
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            lineHeight: 1,
            boxSizing: 'border-box',
          }}
        >
          {displayCount}
        </span>
      )}
    </button>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({
  ctx,
  setCtx,
  pinned,
  setPinned,
  isMobile,
  mobileOpen,
  onMobileClose,
}: {
  ctx: WorkspaceCtx;
  setCtx: (c: WorkspaceCtx) => void;
  pinned: boolean;
  setPinned: (v: boolean) => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const clearUser = useAuthStore((state) => state.clearUser);
  const logoutMutation = useLogout();

  const [hovered, setHovered] = useState(false);
  // On mobile the sidebar is an overlay drawer — always fully expanded,
  // shown/hidden by the `mobile-open` class instead of hover.
  const expanded = isMobile || pinned || hovered;

  const [ctxOpen, setCtxOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('welynk:groups') || '{}');
    } catch {
      return {};
    }
  });

  const user = useAuthStore((state) => state.user);
  useMessagingRealtime(!!user, user?.id);
  const live = useRealtimeConnected();
  const displayName = user?.email ? user.email.split('@')[0] : 'User';
  const email = user?.email ?? '';
  const isAdmin =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'SUPPORT_ADMIN' ||
    user?.role === 'FINANCE_ADMIN' ||
    user?.role === 'CONTENT_ADMIN' ||
    user?.role === 'ADMIN';

  const { selectedOrgName, setWorkspaceContext, clearWorkspaceContext } = useUIStore();

  const {
    data: userOrgs = [],
    isLoading: orgsLoading,
    isSuccess: orgsLoaded,
  } = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => OrganizationApiService.getMyOrganizations(),
    staleTime: 5 * 60 * 1000,
    select: (orgs: UserOrganization[]) => orgs.filter((o) => o.status === 'ACTIVE'),
  });

  // Workspace pages take their organization from the URL (OrgScope). The one
  // remembered for short-form links is checked against the memberships the
  // server reports, so it's forgotten once someone leaves or is removed.
  const workspaceOrgId = useUIStore((s) => s.selectedOrgId);
  useEffect(() => {
    if (!orgsLoaded || !workspaceOrgId) return;
    const membership = userOrgs.find((m) => m.organization.id === workspaceOrgId);
    if (!membership || !isWorkspaceMembership(membership)) clearWorkspaceContext();
  }, [orgsLoaded, userOrgs, workspaceOrgId, clearWorkspaceContext]);

  // College: hide what the member's role can't use, and attach live badges.
  const { selectedOrgId } = useUIStore();
  const { role: collegeRole, permissions: collegePerms } = useCollegeRole();
  const inCollege = ctx === 'college' && !!selectedOrgId;
  // Badges fetch only once the role is known, so members who can't use them never hit a 403.
  const canSeeSetup =
    inCollege && !!collegeRole && canUseCollegeSection(collegeRole, 'setup', collegePerms);
  const canSeeOptOuts =
    inCollege && !!collegeRole && canUseCollegeSection(collegeRole, 'optouts', collegePerms);
  const canSeeReviews =
    inCollege && !!collegeRole && canUseCollegeSection(collegeRole, 'resumeReviews', collegePerms);
  const { data: openReviews } = useQuery({
    queryKey: ['resume-review-queue', selectedOrgId, 'OPEN'],
    queryFn: () => CollegeWorkspaceApiService.reviewQueue(selectedOrgId!, 'OPEN'),
    enabled: canSeeReviews,
    staleTime: 60_000,
    retry: false,
  });
  const waitingReviews = (openReviews ?? []).filter((r) => r.status === 'PENDING').length;
  const { data: setupStatus } = useQuery({
    queryKey: ['college-setup', selectedOrgId],
    queryFn: () => AcademicApiService.setupStatus(selectedOrgId!),
    enabled: canSeeSetup,
    staleTime: 60_000,
    retry: false,
  });
  const { data: pendingOptOuts } = useQuery({
    queryKey: ['org-opt-outs', selectedOrgId, 'NEEDS_REVIEW'],
    queryFn: () => CampusPlacementApiService.orgOptOuts(selectedOrgId!, 'NEEDS_REVIEW'),
    enabled: canSeeOptOuts,
    staleTime: 60_000,
    retry: false,
  });

  // Personal Messages: unread total from the same query (and cache) the Messages page uses.
  const { data: conversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => MessageApiService.getConversations(),
    enabled: ctx === 'personal',
    staleTime: 30_000,
    refetchInterval: live ? 300_000 : 60_000,
    retry: false,
  });
  const unreadMessages = badgeUnread(conversations);

  // College Messages: the staff member's college inbox, same query (and cache) as the page.
  const canSeeCollegeMessages =
    inCollege && !!collegeRole && canUseCollegeSection(collegeRole, 'messages', collegePerms);
  const { data: collegeConversations } = useQuery({
    queryKey: ['college-conversations', selectedOrgId],
    queryFn: () => MessageApiService.getCollegeConversations(selectedOrgId!),
    enabled: canSeeCollegeMessages,
    staleTime: 30_000,
    refetchInterval: live ? 300_000 : 60_000,
    retry: false,
  });
  const unreadCollegeMessages = badgeUnread(collegeConversations);

  // Students of a college get the My College group; everyone else gets "Find your
  // college". Neither shows until memberships load, so nothing flashes and vanishes.
  const isStudent = userOrgs.some(isStudentMembership);
  const showsAudience = (it: NavItem) =>
    !it.audience || (orgsLoaded && (it.audience === 'student') === isStudent);

  const { role: companyRole, permissions: companyPerms } = useCompanyRole();

  // Company Messages: the recruiter's inbox, same query (and cache) as the page.
  const canSeeCompanyMessages =
    ctx === 'company' &&
    !!companyRole &&
    canUseCompanySection(companyRole, 'messages', companyPerms);
  const { data: companyConversations } = useQuery({
    queryKey: ['company-conversations', selectedOrgId],
    queryFn: () => MessageApiService.getCompanyConversations(selectedOrgId!),
    enabled: canSeeCompanyMessages && !!selectedOrgId,
    staleTime: 30_000,
    refetchInterval: live ? 300_000 : 60_000,
    retry: false,
  });
  const unreadCompanyMessages = badgeUnread(companyConversations);

  // Unread messages in the browser tab title: "(3) Messages · Welynk".
  const unreadAllMessages = unreadMessages + unreadCollegeMessages + unreadCompanyMessages;
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\) /, '');
    document.title =
      unreadAllMessages > 0
        ? `(${unreadAllMessages > 99 ? '99+' : unreadAllMessages}) ${base}`
        : base;
  }, [unreadAllMessages]);

  const filteredGroups: NavGroup[] =
    ctx === 'personal'
      ? NAV.personal
          .map((g) => ({
            ...g,
            items: g.items
              .filter(showsAudience)
              .map((it) =>
                it.id === 'messages' && unreadMessages > 0
                  ? { ...it, badge: unreadMessages > 99 ? '99+' : String(unreadMessages) }
                  : it
              ),
          }))
          .filter((g) => g.items.length > 0)
      : ctx === 'company'
        ? NAV.company
            .map((g) => ({
              ...g,
              items: g.items
                .filter(
                  (it) =>
                    !it.companySection ||
                    canUseCompanySection(companyRole, it.companySection, companyPerms)
                )
                .map((it) =>
                  it.companySection === 'messages' && unreadCompanyMessages > 0
                    ? {
                        ...it,
                        badge: unreadCompanyMessages > 99 ? '99+' : String(unreadCompanyMessages),
                      }
                    : it
                ),
            }))
            .filter((g) => g.items.length > 0)
        : NAV.college
            .map((g) => ({
              ...g,
              items: g.items
                .filter(
                  (it) => !it.section || canUseCollegeSection(collegeRole, it.section, collegePerms)
                )
                // The setup checklist leaves the sidebar once it's finished (or while unknown).
                .filter((it) => it.section !== 'setup' || (setupStatus && !setupStatus.completed))
                .map((it) => {
                  if (it.section === 'setup' && setupStatus) {
                    const { left } = setupProgress(setupStatus);
                    // All steps done but not yet confirmed: nudge to finish rather than show nothing.
                    return { ...it, badge: left ? `${left} left` : 'Ready' };
                  }
                  if (it.section === 'resumeReviews' && waitingReviews > 0) {
                    return { ...it, badge: String(waitingReviews) };
                  }
                  if (it.section === 'optouts' && pendingOptOuts?.length) {
                    return { ...it, badge: String(pendingOptOuts.length) };
                  }
                  if (it.section === 'messages' && unreadCollegeMessages > 0) {
                    return {
                      ...it,
                      badge: unreadCollegeMessages > 99 ? '99+' : String(unreadCollegeMessages),
                    };
                  }
                  return it;
                }),
            }))
            .filter((g) => g.items.length > 0);

  // Derive trigger icon + label from current context
  const ctxIcon =
    ctx === 'personal' ? (
      <User size={15} />
    ) : ctx === 'company' ? (
      <Building2 size={15} />
    ) : (
      <GraduationCap size={15} />
    );
  const ctxName =
    ctx === 'personal'
      ? 'Personal workspace'
      : (selectedOrgName ?? (ctx === 'company' ? 'Company' : 'College'));

  // Only the most specific matching item is active, so /college/students/profiles
  // highlights Career Profiles and not Students as well. Items are written in the
  // short form, so compare against the page without the organization slug.
  const pagePath = stripOrgSlug(location.pathname);
  const activeTo = filteredGroups
    .flatMap((g) => g.items.map((it) => it.to))
    .filter((to) => pagePath === to || pagePath.startsWith(`${to}/`))
    .sort((a, b) => b.length - a.length)[0];
  const isActive = (to: string) => to === activeTo;

  const toggleGroup = (g: string) => {
    setCollapsed((c) => {
      const next = { ...c, [g]: !c[g] };
      localStorage.setItem('welynk:groups', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (!expanded) {
      setCtxOpen(false);
      setProfileOpen(false);
    }
  }, [expanded]);

  const handleLogout = useCallback(() => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        clearUser();
        navigate('/login', { replace: true });
      },
      onError: () => {
        clearUser();
        navigate('/login', { replace: true });
      },
    });
  }, [logoutMutation, clearUser, navigate]);

  // Active indicator
  const navRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ top: 0, height: 36, opacity: 0 });
  useEffect(() => {
    if (!navRef.current) return;
    const el = navRef.current.querySelector<HTMLElement>('.wl-nav-item.active');
    if (!el) {
      setIndicator((i) => ({ ...i, opacity: 0 }));
      return;
    }
    const r = el.getBoundingClientRect();
    const pr = navRef.current.getBoundingClientRect();
    setIndicator({ top: r.top - pr.top + navRef.current.scrollTop, height: r.height, opacity: 1 });
  }, [location.pathname, ctx, expanded, collapsed]);

  return (
    <aside
      className={`wl-sidebar ${expanded ? 'expanded' : ''} ${pinned && !isMobile ? 'pinned' : ''} ${
        isMobile && mobileOpen ? 'mobile-open' : ''
      }`}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
    >
      {/* Logo + pin (desktop) / close (mobile drawer) */}
      <div className="wl-sidebar-head">
        <Link to={NAV[ctx][0].items[0].to} className="wl-sidebar-brand">
          <span className="wl-logo">
            <span className="we">we</span>
            {expanded && <span className="lynk">lynk</span>}
          </span>
        </Link>
        {expanded && isMobile && (
          <button className="wl-sidebar-pin" onClick={onMobileClose} aria-label="Close menu">
            <X size={16} />
          </button>
        )}
        {expanded && !isMobile && (
          <button
            className="wl-sidebar-pin"
            onClick={() => {
              const v = !pinned;
              setPinned(v);
              localStorage.setItem('welynk:sidebar-pinned', v ? '1' : '0');
            }}
            title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
          >
            <Pin
              size={14}
              style={{ transform: pinned ? 'rotate(45deg)' : 'none', transition: 'transform .15s' }}
            />
          </button>
        )}
      </div>

      {/* Context switcher */}
      <div className="wl-ctx-wrap">
        <button
          className="wl-ctx-card"
          onClick={() => !orgsLoading && setCtxOpen((o) => !o)}
          style={orgsLoading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
        >
          <div className="wl-ctx-ic">{ctxIcon}</div>
          {expanded && (
            <>
              <div className="wl-ctx-meta">
                <div className="wl-ctx-label">Current context</div>
                <div className="wl-ctx-name">{orgsLoading ? 'Loading…' : ctxName}</div>
              </div>
              <ChevronDown
                size={14}
                className="wl-ctx-chev"
                style={{ transform: ctxOpen ? 'rotate(180deg)' : 'none' }}
              />
            </>
          )}
        </button>

        {ctxOpen && expanded && (
          <div
            className="wl-menu"
            style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4 }}
          >
            <div className="wl-menu-label">Switch context</div>

            {/* Personal workspace */}
            <div
              className="wl-menu-item"
              onClick={() => {
                clearWorkspaceContext();
                setCtx('personal');
                setCtxOpen(false);
                navigate('/personal/dashboard');
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: 'var(--bg-2)',
                  color: 'var(--ink-2)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <User size={15} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>Personal workspace</div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Career &amp; job search</div>
              </div>
              {ctx === 'personal' && !(isStudent && location.pathname.startsWith(STUDENT_HOME)) && (
                <Check size={14} style={{ color: 'var(--teal-3)' }} />
              )}
            </div>

            {/* User's organizations */}
            {userOrgs.length > 0 && (
              <>
                <div className="wl-menu-sep" />
                <div className="wl-menu-label">Organizations</div>
                {userOrgs.map((uo) => {
                  const orgCtx: WorkspaceCtx =
                    uo.organization.type === 'COMPANY' ? 'company' : 'college';
                  // A student's college opens their College Home, not the staff workspace.
                  const asStudent = isStudentMembership(uo);
                  const isSelected = asStudent
                    ? location.pathname.startsWith(STUDENT_HOME) &&
                      (new URLSearchParams(location.search).get('org') ?? uo.organization.id) ===
                        uo.organization.id
                    : ctx !== 'personal' && workspaceOrgId === uo.organization.id;
                  const OrgIcon = uo.organization.type === 'COMPANY' ? Building2 : GraduationCap;
                  return (
                    <div
                      key={uo.organization.id}
                      className="wl-menu-item"
                      onClick={() => {
                        setCtxOpen(false);
                        if (asStudent) {
                          clearWorkspaceContext();
                          setCtx('personal');
                          navigate(`${STUDENT_HOME}?org=${uo.organization.id}`);
                          return;
                        }
                        setWorkspaceContext(orgCtx, {
                          id: uo.organization.id,
                          name: uo.organization.name,
                          type: uo.organization.type,
                          logoUrl: uo.organization.logoUrl,
                          slug: uo.organization.slug,
                        });
                        setCtx(orgCtx);
                        navigate(membershipHome(uo));
                      }}
                    >
                      {uo.organization.logoUrl ? (
                        <img
                          src={uo.organization.logoUrl}
                          alt={uo.organization.name}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: 'var(--bg-2)',
                            color: 'var(--ink-2)',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <OrgIcon size={15} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {uo.organization.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--ink-4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          {uo.organization.type === 'COMPANY' ? 'Company' : 'Institution'}
                          {' · '}
                          {memberRoleLabel(uo.role)}
                          {uo.organization.verificationStatus === 'APPROVED' && (
                            <CheckCircle2
                              size={10}
                              style={{ color: 'var(--teal-3)', flexShrink: 0 }}
                            />
                          )}
                        </div>
                      </div>
                      {isSelected && <Check size={14} style={{ color: 'var(--teal-3)' }} />}
                    </div>
                  );
                })}
              </>
            )}

            {/* Create new organization */}
            <div className="wl-menu-sep" />
            <div
              className="wl-menu-item"
              style={{ color: 'var(--teal-3)' }}
              onClick={() => {
                setCtxOpen(false);
                navigate('/organization/create');
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: 'var(--bg-2)',
                  color: 'var(--teal-3)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Plus size={15} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>Create new organization</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div ref={navRef} className="wl-nav wl-scroll">
        {expanded && (
          <span
            className="wl-active-indicator"
            style={{ top: indicator.top, height: indicator.height, opacity: indicator.opacity }}
          />
        )}

        {filteredGroups.length === 0 && (
          <div
            style={{
              padding: '32px 12px',
              textAlign: 'center',
              color: 'var(--ink-4)',
              fontSize: 12.5,
            }}
          >
            No navigation items
          </div>
        )}

        {filteredGroups.map((group, gi) => (
          <div className="wl-nav-group" key={gi}>
            {expanded && (
              <button
                className="lbl"
                onClick={() => toggleGroup(group.group)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span>{group.group}</span>
                <ChevronDown
                  size={10}
                  style={{
                    transition: 'transform .15s',
                    transform: collapsed[group.group] ? 'rotate(-90deg)' : 'none',
                  }}
                />
              </button>
            )}

            {(!expanded || !collapsed[group.group]) &&
              group.items.map((it) => (
                <Link
                  key={it.id}
                  to={it.to}
                  className={`wl-nav-item wl-has-tip ${isActive(it.to) ? 'active' : ''}`}
                  data-tip={it.label}
                  aria-current={isActive(it.to) ? 'page' : undefined}
                  aria-label={expanded ? undefined : it.label}
                >
                  <span className="wl-nav-ic" aria-hidden="true">
                    {it.icon}
                  </span>
                  {expanded && (
                    <>
                      <span className="wl-nav-label">{it.label}</span>
                      {it.soon ? (
                        <span className="wl-nav-badge soon">Soon</span>
                      ) : (
                        it.badge && <span className="wl-nav-badge">{it.badge}</span>
                      )}
                    </>
                  )}
                </Link>
              ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="wl-sidebar-foot">
        <button className="wl-profile-card" onClick={() => setProfileOpen((o) => !o)}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <WlAvatar name={displayName} size="sm" />
            <span className="wl-profile-status" />
          </div>
          {expanded && (
            <>
              <div className="wl-pmeta">
                <div className="wl-pname">{displayName}</div>
                <div className="wl-pemail">{email}</div>
              </div>
              <ChevronDown
                size={14}
                className="wl-profile-chev"
                style={{
                  color: 'var(--ink-4)',
                  transform: profileOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform .15s',
                }}
              />
            </>
          )}
        </button>

        {profileOpen && expanded && (
          <div
            className="wl-menu wl-fade-in"
            style={{ position: 'absolute', bottom: '100%', left: 10, right: 10, marginBottom: 4 }}
          >
            <div className="wl-profile-menu-head">
              <WlAvatar name={displayName} size="md" />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{displayName}</div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: 'var(--ink-4)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {email}
                </div>
              </div>
            </div>
            <div className="wl-menu-sep" />
            <div
              className="wl-menu-item"
              onClick={() => {
                navigate('/personal/profile');
                setProfileOpen(false);
              }}
            >
              <User size={14} /> My profile
            </div>
            {isAdmin && (
              <div
                className="wl-menu-item"
                style={{ color: 'var(--teal-6, #2f8f83)' }}
                onClick={() => {
                  navigate('/admin');
                  setProfileOpen(false);
                }}
              >
                <ShieldCheck size={14} /> Admin Panel
              </div>
            )}
            <div
              className="wl-menu-item"
              onClick={() => {
                navigate('/personal/settings');
                setProfileOpen(false);
              }}
            >
              <Settings size={14} /> Settings
            </div>
            <div
              className="wl-menu-item"
              onClick={() => {
                navigate('/personal/subscription-and-billing');
                setProfileOpen(false);
              }}
            >
              <CreditCard size={14} /> Subscription &amp; billing
            </div>
            <div
              className="wl-menu-item"
              onClick={() => {
                navigate('/help');
                setProfileOpen(false);
              }}
            >
              <HelpCircle size={14} /> Help &amp; support
            </div>
            <div className="wl-menu-sep" />
            <div className="wl-menu-item" style={{ color: 'var(--danger)' }} onClick={handleLogout}>
              <LogOut size={14} />
              {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// ─── Plan badge ───────────────────────────────────────────────────────────────

const BILLING_PATH_BY_CTX: Record<WorkspaceCtx, string> = {
  personal: '/personal/subscription-and-billing',
  company: '/company/billing',
  college: '/college/billing',
};

function PlanBadge({ ctx }: { ctx: WorkspaceCtx }) {
  const navigate = useNavigate();
  const { selectedOrgId } = useUIStore();
  const organizationId = ctx === 'personal' ? undefined : (selectedOrgId ?? undefined);
  // An organization's plan is billing data: only owners and admins may read it,
  // so other members (faculty, recruiters…) get no badge instead of a 403.
  const { role, permissions } = useSelectedOrgRole();
  const canSeePlan =
    ctx === 'personal' ||
    role === 'OWNER' ||
    role === 'ADMIN' ||
    !!permissions?.includes('ORG_MANAGE_BILLING');
  const { data: overview, isLoading } = useBillingOverview(organizationId, { enabled: canSeePlan });

  // Org contexts with no workspace selected yet — nothing meaningful to show.
  if (ctx !== 'personal' && (!organizationId || !canSeePlan)) return null;
  if (isLoading)
    return (
      <span className="wl-pill" style={{ opacity: 0.5 }} aria-hidden="true">
        ···
      </span>
    );

  const plan = overview?.subscription?.plan;
  const isPaid = plan?.priceMonthly != null;
  const label = plan?.name ?? 'Free';

  return (
    <button
      className={`wl-pill ${isPaid ? 'teal' : ''}`}
      style={{ border: 'none', cursor: 'pointer' }}
      onClick={() => navigate(BILLING_PATH_BY_CTX[ctx])}
      aria-label={`Current plan: ${label}. Click to manage billing.`}
    >
      <span className="dot" aria-hidden="true" />
      {label}
    </button>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

function AppHeader({
  onSearchOpen,
  onMenuOpen,
}: {
  onSearchOpen: () => void;
  onMenuOpen: () => void;
}) {
  const location = useLocation();
  const crumbs = resolveCrumbs(location.pathname);
  const ctx = getCtxFromPath(location.pathname);
  // Connections are a personal-workspace feature — hide the hub in org contexts.
  const isPersonal = ctx === 'personal';

  return (
    <div className="wl-app-header">
      <button
        className="wl-btn-icon wl-mobile-menu-btn"
        onClick={onMenuOpen}
        aria-label="Open navigation menu"
      >
        <Menu size={18} />
      </button>
      <div className="wl-crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight size={12} className="sep" />}
            <span className={i === crumbs.length - 1 ? 'now' : ''}>{c}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="wl-header-actions">
        <button className="wl-header-search" onClick={onSearchOpen} aria-label="Search">
          <Search size={14} />
          <span>Search…</span>
          <span className="wl-kbd-hint">⌘K</span>
        </button>
        <PlanBadge ctx={ctx} />
        {isPersonal && <ConnectHubButton />}
        <NotificationBell />
      </div>
    </div>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

export function AppShell() {
  const location = useLocation();
  const isMobile = useIsMobile();

  const [ctx, setCtx] = useState<WorkspaceCtx>(() => getCtxFromPath(location.pathname));
  const [pinned, setPinned] = useState(() => localStorage.getItem('welynk:sidebar-pinned') === '1');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Sync ctx when route changes; any navigation also dismisses the mobile drawer
  useEffect(() => {
    const want = getCtxFromPath(location.pathname);
    setCtx(want);
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!(isMobile && mobileNavOpen)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileNavOpen]);

  // ⌘K shortcut + Escape closes the mobile drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="wl-shell">
      {isMobile && mobileNavOpen && (
        <div className="wl-sidebar-backdrop" onClick={() => setMobileNavOpen(false)} />
      )}
      <Sidebar
        ctx={ctx}
        setCtx={setCtx}
        pinned={pinned}
        setPinned={setPinned}
        isMobile={isMobile}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <div
        className="wl-shell-main"
        style={{
          marginLeft: isMobile ? 0 : pinned ? 'var(--sidebar-w)' : 'var(--sidebar-w-collapsed)',
        }}
      >
        <AppHeader
          onSearchOpen={() => setSearchOpen(true)}
          onMenuOpen={() => setMobileNavOpen(true)}
        />
        <main style={{ flex: 1 }}>
          {/* Pages are lazy chunks (routes/lazy.tsx) — the boundary here keeps
              the shell chrome mounted while a chunk downloads. */}
          <React.Suspense fallback={<RouteFallback />}>
            <Outlet />
          </React.Suspense>
        </main>
      </div>

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

// ─── Shared page helpers ──────────────────────────────────────────────────────
// The Wl* primitives (WlPage, WlSection, WlStat, WlAvatar, WlProgressBar,
// WlPlaceholder) live in WlPrimitives.tsx; re-exported here so the 40+
// existing `from '@/components/layouts/AppShell'` imports keep working.

export {
  WlPage,
  WlStat,
  WlSection,
  WlProgressBar,
  WlPlaceholder,
  WlAvatar,
  WlTabList,
} from './WlPrimitives';
